import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import set from 'date-fns/set';
import startOfWeek from 'date-fns/startOfWeek';
import type { IntlShape } from 'react-intl';

import { OPERATIONS } from '../constants';
import {
	AVAILABLE_RULE_TYPES,
	type Operation,
	type OperationIds,
	type RuleType,
	type TimeRestriction,
} from '../types';
import notificationSectionMessages from '../ui/notification-section/messages';

export const getOperationName = (id: OperationIds): Operation => {
	const operation = OPERATIONS.find((operation) => operation.id === id);
	return operation?.name as Operation;
};

export const getMatchType = (
	conditionMatchType: number,
): 'match-any-condition' | 'match-all-conditions' | 'match-all' => {
	switch (conditionMatchType) {
		case 1:
			return 'match-any-condition';
		case 2:
			return 'match-all-conditions';
		default:
			return 'match-all';
	}
};

export const getDays = () => {
	const firstDOW = startOfWeek(new Date(), {
		weekStartsOn: 0,
	});

	const weekDays = Array.from(Array(7)).map((e, i) => ({
		label: format(addDays(firstDOW, i), 'EEEE'),
		value: (i % 7) + 1,
	}));

	return weekDays as {
		label: string;
		value: 1 | 2 | 3 | 4 | 5 | 6 | 7;
	}[];
};

export const formatRestriction = (timeInterval: TimeRestriction): string => {
	const days = getDays();

	const startDay = days.find((day) => day.value === timeInterval.startDay);
	const endDay = days.find((day) => day.value === timeInterval.endDay);

	const startTime = format(
		set(new Date(), {
			hours: timeInterval.startHour,
			minutes: timeInterval.startMinute,
		}),
		'p',
	);

	const endTime = format(
		set(new Date(), {
			hours: timeInterval.endHour,
			minutes: timeInterval.endMinute,
		}),
		'p',
	);

	if (startDay && endDay) {
		const formattedTime = `${startDay.label} ${startTime} - ${endDay.label} ${endTime}`;

		return formattedTime;
	}

	const formattedTime = `${startTime} - ${endTime}`;

	return formattedTime;
};

export const getAvailableRuleTypeOptions = (
	formatMessage: IntlShape['formatMessage'],
): {
	label: string;
	value: RuleType;
}[] => {
	return Object.keys(AVAILABLE_RULE_TYPES)
		.filter((v) => isNaN(Number(v)))
		.map((key) => ({
			label: formatMessage(notificationSectionMessages[key]),
			value: key as RuleType,
		}));
};

export const getDefaultTimeOptions = (): string[] => {
	return [...Array(24).keys()]
		.map((time) => {
			let newTime = time.toString();
			if (newTime.length === 1) {
				newTime = `0${time}`;
			}
			return [`${newTime}:00`, `${newTime}:30`];
		})
		.flatMap((time) => time);
};

export const getTargetAccountIdSuffix = (targetAccountId: string | null | undefined): string => {
	return targetAccountId ? `?targetAccountId=${targetAccountId}` : '';
};
